/**
 * CR283: Merchant Duplicates Table Component
 */

import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, AlertTriangle } from "lucide-react";
import type { MerchantDuplicate } from "@/hooks/useMerchantsDirectory";

interface MerchantDuplicatesTableProps {
  duplicates: MerchantDuplicate[];
  loading: boolean;
}

export function MerchantDuplicatesTable({ duplicates, loading }: MerchantDuplicatesTableProps) {
  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-warning" />
          Potential Duplicates
        </CardTitle>
        <CardDescription>
          Merchants with similar normalized names that may need to be merged or hidden.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Normalized Name</TableHead>
              <TableHead>Count</TableHead>
              <TableHead>Merchants</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {duplicates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No duplicate merchants found
                </TableCell>
              </TableRow>
            ) : (
              duplicates.map((dup) => (
                <TableRow key={dup.normalized_name}>
                  <TableCell className="font-mono text-sm">{dup.normalized_name}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{dup.cnt}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {dup.display_names.map((name, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {name}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {dup.merchant_keys.map((key) => (
                        <Button key={key} variant="ghost" size="sm" asChild>
                          <Link to={`/admin/merchants/${key}`}>
                            <ExternalLink className="h-3 w-3 mr-1" />
                            {key}
                          </Link>
                        </Button>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
